<template>
  <div class="document">
    <div class="header">
      <EsHeader>
        <template #header>
          <div class="header-title">
            Data Mart Synchronization Details
          </div>
        </template>
      </EsHeader>
    </div>
    <div class="content">
      <es-loader v-if="loading" />
      <es-exception v-else-if="loadingError"
        exceptionMessage="There was an issue. Please try again or contact your administrator." />
      <div v-else class="align-left">
        <table>
          <tbody>
            <tr>
              <th colspan="2" class="category">
                {{ datamart.label }}
              </th>
            </tr>
            <tr>
              <td class="has-text-grey" style="width: 625px">
                Destination Mart Name
              </td>
              <td class="text">
                <div class="control">
                  <input class="input is-static" :value="datamart.datamartType.name" readonly
                    aria-label="Destination Mart Name"
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td class="has-text-grey" style="vertical-align: middle; width: 50%">
                Destination Table Name
              </td>
              <td>
                <div class="control">
                  <input class="input is-static"
                      :value="datamart.tableName" readonly
                      aria-label="Destination Table Name"
                  />
                </div>
              </td>
            </tr>
            <tr>
              <td class="has-text-grey" style="vertical-align: middle; width: 50%">
                Last Synchronization Date
              </td>
              <td class="text">
                <div class="control">
                  <span class="input is-static">{{ datamart.syncTime | formatDate }}</span>
                </div>
              </td>
            </tr>
            <tr>
              <td class="has-text-grey" style="vertical-align: middle; width: 50%">
                Synchronization Type
              </td>
              <td style="font-size:14px;">
                Re-Synch By Date /Time
              </td>
            </tr>
            <tr>
              <td class="has-text-grey" style="vertical-align: middle; width: 50%">
                Synch From Date
              </td>
              <td>
                <div class="control">
                   <input
                    class="input calendar"
                    :class="{ 'placeholder': !synchFromDate }"
                    type="date"
                    v-model="synchFromDate"
                    @change.prevent="validateField('synchFromDate')"
                    aria-label="Synch From Date"
                  />
                  <span v-if="hasErrorSynchFromDate" class="help is-danger">
                    {{ msgSynchFromDate }}
                  </span>
                </div>
              </td>
            </tr>
            <tr>
              <td class="has-text-grey" style="vertical-align: middle; width: 50%">
                Synch From Time
              </td>
              <td style="display: flex; align-items: center;">
                <div class="select">
                  <select class="input" v-model="hourOption" aria-label="Synch From Time - Hour">
                    <option
                      v-for="(hour, index) in getHourOptions()"
                      :key="index"
                      :value="hour"
                      :selected="hour === preSelectedHour"
                    >
                      {{ hour }}
                    </option>
                  </select>
                </div>
                <strong style="margin: 0 5px;">:</strong>
                <div class="select">
                  <select class="input" v-model="minuteOption" aria-label="Synch From Time - Minute">
                    <option
                      v-for="(minute, index) in getMinuteOptions()"
                      :key="index"
                      :value="minute"

                    >
                      {{ minute }}
                    </option>
                  </select>
                </div>
                <div class="am-pm-options">
                  <button
                    class="button button--form"
                    v-for="option in amPmOptions"
                    :key="option.description"
                    @click="amPmType = option.description"
                    :class="{ 'is-active': amPmType == option.description }"
                  >
                    {{ option.description }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <span style="font-size: 12px;">
          This will synchronize data that was updated or inserted on or after the Synch From Date and Time.
        </span>
      </div>
      <div v-if="!loading && !loadingError" class="is-flex flex-justify--end flex-align--center actions-container">
        <button class="button button-link has-underline" @click.prevent="cancelPanel()" style="margin-right: 1rem">
          Cancel
        </button>
        <button class="button is-primary" @click.prevent="resynchDatamart()">
          Resynchronize Data Mart
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import EsException from '@/components/EsException.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import moment from 'moment';
import NotificationManager from '@/mixins/NotificationManager';
import DateUtils from '@/mixins/DateUtils';

export default {
  mixins: [
    NotificationManager,
    DateUtils,
  ],
  components: {
    EsException,
    EsHeader,
    EsLoader,
  },
  props: {
    datamartObject: {
      type: Object,
    },
  },
  watch: {
  amPmType() {
    this.calculateTime();
    },
  },
  created() {
    this.amPmOptions = [
      {
        description: 'AM',
      },
      {
        description: 'PM',
      },
    ];
  },
  mounted() {
    this.hourOption = this.preSelectedHour;
    this.minuteOption = this.preSelectedMin;
  },
  data() {
    return this.initialData();
  },
  computed: {
    preSelectedHour() {
      return '12';
    },
    preSelectedMin() {
      return '00';
    },
    calculateTime() {
      const hour = parseInt(this.hourOption, 10);
      const minute = parseInt(this.minuteOption, 10);
      let updatedHour = hour;
      if (this.amPmType === 'PM' && hour < 12) {
        updatedHour += 12;
      } else if (this.amPmType === 'AM' && hour === 12) {
        updatedHour = 0;
      }
      this.syncTime = updatedHour * 100 + minute; // Assign the calculated time to syncTime
    },
  },
  methods: {
    initialData() {
      return {
        loading: false,
        loadingError: false,
        amPmType: 'AM',
        hourOption: null,
        minuteOption: null,
        datamart: this.datamartObject,
        synchFromDate: null,
        syncTime: null,
        hasErrorSynchFromDate: false,
        msgSynchFromDate: '',
      };
    },
    getHourOptions() {
      const options = [];
      for (let i = 1; i <= 12; i += 1) {
        options.push(i.toString());
      }
      return options;
    },
    getMinuteOptions() {
      const options = [];
      for (let i = 0; i <= 59; i += 1) {
        options.push(i.toString().padStart(2, '0'));
      }
      return options;
    },
    cancelPanel() {
      this.$emit('dataMartList');
    },
    hasFieldErrors() {
      this.validateField('synchFromDate');
      const err = this.hasErrorSynchFromDate;
      if (err) {
        this.showNotification('danger', ['There were some errors.Please fix the errors highlighted in the fields below.']);
      } else {
        this.hideNotification();
      }

      return err;
    },
   async resynchDatamart() {
      if (this.hasFieldErrors()) return;
      this.calculateTime;
      const hour = Math.floor(this.syncTime / 100);
      const minute = this.syncTime % 100;
      const formattedHour = hour.toString().padStart(2, '0');
      const formattedMinute = minute.toString().padStart(2, '0');
      const syncDateTime = `${this.synchFromDate} ${formattedHour}:${formattedMinute}`;
      const formattedSyncDateTime = moment(syncDateTime, 'YYYY-MM-DD HH:mm').toDate();
      try {
        await axios.put(`datamart/destination/${this.datamart.id}/resync`, {
          syncTime: formattedSyncDateTime,
          typeId: this.datamart.id,
          label: this.datamart.label
        });
         this.$emit('syncConfirmation')
      } catch (error) {
        if (error.response.status === 400) {
          this.apiMsg = 'There was an issue. Please try again or contact your administrator.';
        } else {
          this.apiMsg = 'There were some errors. Please fix the errors highlighted in the fields below.';
        }
        this.showNotification('danger', [this.apiMsg]);
      }
    },
    validateField(field) {
      if (field === 'synchFromDate') {
        if (this.synchFromDate === '') {
          this.hasErrorSynchFromDate = true;
          this.msgSynchFromDate = 'Synch From Date is required.';
        } else if (!this.isValidDateFormat(this.synchFromDate, 'YYYY-MM-DD')) {
            this.hasErrorSynchFromDate = true;
            this.msgSynchFromDate = 'Synch From Date is invalid.';
        } else {
            this.hasErrorSynchFromDate = false;
            this.msgSynchFromDate = '';
        }
      }
    },
  },
  filters: {
    formatDate(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY HH:mm:ss');
      }
      return 'N/A';
    },
  },
};
</script>

<style lang="sass" scoped>
  table td
    font-size: 12px
    vertical-align: middle
  .content
    padding: 0 3rem 0.5rem
    overflow-x: hidden
  .actions-container
    padding: 2rem 0
  .header
    display: flex
    align-items: center
  .text
    text-align: center
    font-size: 14px
  .am-pm-options
    margin: 0 1rem
    display: flex
  .align-left
    text-align: left
</style>
